/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { cloneDeep, forEach, isEmpty, isNil, omit, map, get } from 'lodash';
import * as React from 'react';
import { useState, useContext } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, AlertBar, Upload, uploadSingleFile } from '@blend/enterprise-components';
import { useParams } from 'react-router-dom';

import { addDocs, addDocsToLending } from 'blb-api/loans';
import { MeContext } from 'blb-contexts/Me';
import { completeTask } from 'blb-api/tasks';
import { uploadFile } from 'blb-api/files';
import { baseRequest } from 'blb-api/baseRequest';
import { DisablednessContext } from 'blb-contexts/Disabledness';
import { errorMessage } from 'blb-utils/errors';
import { DocumentType, TITLE_WIRE_INSTRUCTIONS_DOC_TYPE } from 'blb-constants/DocumentTypes';

import { FileUploader } from './FileUploader';
import { UploadItem } from './UploadItem';
import { UploadItemWithType } from './UploadItemWithType';
import { canSubmit } from './utils';
import type { UploadedDocument } from './DocUploadModal';
import { BlendBridge } from 'blb-types/blendBridge';

const hasExceededUploadLimit = (
  uploadMap: Record<string, { file: File; fileSizeInMB: number; type?: string; upload: Upload }>,
  uploadLimit: BlendBridge.API.UploadLimit,
  totalUploadSize: number,
): boolean => {
  // Only pass uploadLimit (from eClose) when closingDesignation is SIGN_AHEAD
  const maxFileSize = Math.max(...Object.keys(uploadMap).map(fileId => uploadMap[fileId].fileSizeInMB));
  return totalUploadSize > uploadLimit.multipleDocsLimitInMB || maxFileSize > uploadLimit.singleDocLimitInMB;
};

const hasViolatedAllowedFileTypes = (
  uploadMap: Record<string, { file: File; fileSizeInMB: number; type?: string; upload: Upload }>,
  allowedFileExtensions: BlendBridge.AllowedFileExtensions[],
): boolean => {
  if (allowedFileExtensions) {
    const allowedExtensions = new Set(Object.values(allowedFileExtensions).map(ext => ext.split('.').pop()));
    const fileExtensions = Object.keys(uploadMap).map(fileId => uploadMap[fileId].file.name.split('.').pop());
    if (fileExtensions) {
      return fileExtensions.some(ext => !allowedExtensions.has(ext));
    }
  }
  return false;
};

export namespace DocUploader {
  export interface Props {
    afterTask?: { buttonText: string; fetchUrl: string };
    allowMultipleUploads?: boolean;
    allowedFileExtensions?: BlendBridge.AllowedFileExtensions[];
    closingDesignation?: EClose.DocumentClosingDesignation;
    documentTargets?: string[]; // To overwrite the document targets field
    documentType?: string;
    handleClose: () => void;
    onSuccess?: (documents: UploadedDocument[]) => Promise<void> | void;
    selectableDocTypes?: DocumentType[];
    sourceType?: EClose.SourceType;
    taskId?: string;
    uploadLimit?: BlendBridge.API.UploadLimit;
  }
}

const styles = StyleSheet.create({
  addButtonText: {
    marginLeft: 13,
  },
  errorContainer: {
    marginBottom: 8,
  },
  fileUploaderWrapper: {
    marginTop: 12,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

export const DocUploader: React.FC<DocUploader.Props> = ({
  afterTask,
  uploadLimit,
  allowMultipleUploads,
  closingDesignation,
  documentType,
  handleClose,
  onSuccess,
  selectableDocTypes,
  sourceType,
  taskId,
  documentTargets,
  allowedFileExtensions,
}) => {
  const { loanId } = useParams<{ loanId: string }>();
  const [uploadMap, setUploadMap] = useState<
    Record<string, { file: File; fileSizeInMB: number; type?: string; upload: Upload }>
  >({});
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [afterTaskUrl, setAfterTaskUrl] = useState<string | undefined>(undefined);
  const [totalUploadSize, setTotalUploadSize] = useState<number>(0);
  const { setDisabled, disabled } = useContext(DisablednessContext);
  const me = useContext(MeContext);

  const removeUpload = (fileId: string) => {
    const upload = uploadMap[fileId];
    setTotalUploadSize(totalUploadSize - upload.fileSizeInMB);
    setUploadMap(uploads => omit(uploads, fileId));
    setError(undefined);
  };

  const handleUploadEvent = (fileId: string, file: File, upload: Upload) => {
    setError(undefined);
    const fileSize = getFileSizeInMB(file.size);

    setUploadMap(uploads => ({
      ...uploads,
      [fileId]: {
        // we specifically overwrite `file` and `upload` and leave
        // everything else as is, e.g. `type`
        ...uploads[fileId],
        file,
        fileSizeInMB: fileSize,
        upload,
      },
    }));

    setTotalUploadSize(totalUploadSize + fileSize);
  };

  const getFileSizeInMB = (bytes: number, decimals = 2): number => {
    // from https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
    return parseFloat((bytes / Math.pow(1024, 2)).toFixed(decimals));
  };

  const isSubmitDisabled = !canSubmit(uploadMap) || isEmpty(uploadMap);
  // for some document types, we only allow a single file upload
  const showFileUploader = allowMultipleUploads || isEmpty(uploadMap);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      setDisabled(true);
      if (allowedFileExtensions) {
        const hasViolated = hasViolatedAllowedFileTypes(uploadMap, allowedFileExtensions);
        if (hasViolated) {
          setError(
            `Cannot upload files. Please select one of allowed file types - ${Object.values(allowedFileExtensions)}`,
          );
          setDisabled(false);
          setSubmitting(false);
          return;
        }
      }
      if (uploadLimit && hasExceededUploadLimit(uploadMap, uploadLimit, totalUploadSize)) {
        setError(uploadLimit.errorMessage);
        setDisabled(false);
        setSubmitting(false);
        return;
      }

      const uploads = map(uploadMap, ({ upload, type }) => ({
        fileKey: upload.url!,
        fileName: upload.fileName!,
        targets: documentTargets,
        type,
      }));
      const uploadedDocuments: UploadedDocument[] = [];
      if (me?.principalType === 'title_agent') {
        // This flow is only being used by Shared Inbox and Wire Instruction
        const files = map(uploads, upload => {
          const file = {
            ...upload,
            type: upload.type || documentType,
            // currently shared inbox is only being sent to lenders, this does not block title agents from viewing the doc
            viewPermission: 'lenders only' as DM.DocumentViewPermission,
          };
          if (file.type === TITLE_WIRE_INSTRUCTIONS_DOC_TYPE) {
            // if we are sending Wire Instruction, this doc should be shared with borrowers
            file.viewPermission = 'all users';
          }
          return file;
        });
        // Title uploads use addDocsToLending (e.g. wire instructions, loan proceeds)
        const { data } = await addDocsToLending({ files, loanId });
        const docIds = data.map(d => d.documentId);
        forEach(data, (value, index) => {
          uploadedDocuments.push({
            documentId: value.documentId,
            name: files[index].fileName,
          });
        });
        // NOTE: taskId should probably be required
        if (taskId) {
          await completeTask(taskId, { meta: { docIds } });
        }
      } else {
        // this function is for EClose uploads
        await addDocs({ closingDesignation, documentType, files: uploads, loanId, sourceType });
      }

      if (afterTask) {
        const { data: url } = await baseRequest.get<string>(afterTask.fetchUrl);
        return setAfterTaskUrl(url);
      }

      if (onSuccess) {
        await onSuccess(uploadedDocuments);
      }
      setDisabled(false);
      handleClose();
    } catch (err) {
      setSubmitting(false);
      setDisabled(false);
      setError(get(err, 'response.data.error.message', errorMessage('UPLOADING_DOCUMENTS', me?.principalType)));
    }
  };

  const handleChangeDocType = (id: string, type: string) => {
    const newUploads = cloneDeep(uploadMap);
    newUploads[id].type = type;
    setUploadMap(newUploads);
  };

  const retryUpload = async (fileId: string) => {
    const { file } = uploadMap[fileId];
    await uploadSingleFile({ file, fileId, handleUploadEvent, otherUploadParams: { loanId }, uploadFile });
  };

  const afterTaskOnClick = () => {
    location.assign(afterTaskUrl!);
  };

  const renderUploadItems = () => {
    if (!isNil(selectableDocTypes)) {
      return map(uploadMap, ({ upload }, fileId) => (
        <UploadItemWithType
          handleChangeDocType={handleChangeDocType}
          id={fileId}
          key={fileId}
          onRemove={() => removeUpload(fileId)}
          onRetry={async () => await retryUpload(fileId)}
          selectableDocTypes={selectableDocTypes}
          upload={upload}
        />
      ));
    } else {
      return map(uploadMap, ({ upload, fileSizeInMB }, fileId) => (
        <UploadItem
          fileSize={fileSizeInMB}
          id={fileId}
          key={fileId}
          onRemove={() => removeUpload(fileId)}
          onRetry={async () => await retryUpload(fileId)}
          upload={upload}
        />
      ));
    }
  };

  return (
    <div>
      <div>
        {error && (
          <div className={css(styles.errorContainer)}>
            <AlertBar message={error} theme="error" />
          </div>
        )}
        <div>
          {renderUploadItems()}
          {showFileUploader && (
            <div className={css(styles.fileUploaderWrapper)}>
              <FileUploader
                allowedFileExtensions={allowedFileExtensions}
                disabled={disabled}
                handleUploadEvent={handleUploadEvent}
                id="it-FileUploader"
                loanId={loanId}
                multiple
                uploadFile={uploadFile}
              />
            </div>
          )}
        </div>
      </div>
      <div className={css(styles.footer)}>
        {!afterTaskUrl && (
          <ActionButton
            disabled={isSubmitDisabled}
            id="it-DocUploader-upload"
            isLoading={isSubmitting}
            label="Upload"
            onClick={handleSubmit}
            theme="primary"
          />
        )}
        {!!afterTaskUrl && (
          <ActionButton
            id="it-DocUploader-afterTaskButton"
            label={afterTask!.buttonText}
            onClick={afterTaskOnClick}
            theme="primary"
          />
        )}
      </div>
    </div>
  );
};
